/* =========================================================================
 * <image-picker-set>
 *
 * Generic count-controlled image picker repeater.
 * - A count selector at the top chooses how many rows to display.
 * - Each row is a custom dropdown with image + title + subtitle per option.
 * - Hidden rows have their cart-property hidden inputs disabled, so they
 *   never reach the cart.
 * - All visible rows are required by default; invalid rows render their own
 *   inline error state. Parent containers (e.g. input-modal) call .validate()
 *   during form submission to gate add-to-cart.
 *
 * Public API:
 *   .validate() → boolean   true when every visible row has a selection.
 *   .reset()                clears every row's selection and rebuilds visibility
 *                           based on the count selector.
 * ========================================================================= */
if (!customElements.get('image-picker-set')) {
  customElements.define(
    'image-picker-set',
    class ImagePickerSet extends HTMLElement {
      connectedCallback() {
        this.maxCount = Math.max(1, parseInt(this.dataset.maxCount, 10) || 5);
        this.minCount = 1;
        this.itemPlaceholder = this.dataset.itemPlaceholder || 'Select…';
        this.errorMessage = this.dataset.errorMessage || 'This field is required.';

        this.countSelect = this.querySelector('[data-picker-count]');
        this.rowsContainer = this.querySelector('[data-picker-rows]');
        this.rows = Array.from(this.querySelectorAll('[data-picker-row]'));
        this.countErrorEl = this.querySelector('[data-picker-count-error]');

        this.boundDocumentClick = this.onDocumentClick.bind(this);
        this.boundKeydown = this.onKeydown.bind(this);

        this.bindCountChange();
        this.bindDropdowns();
        document.addEventListener('click', this.boundDocumentClick);
        document.addEventListener('keydown', this.boundKeydown);

        this.applyCount();
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.boundDocumentClick);
        document.removeEventListener('keydown', this.boundKeydown);
      }

      /* ---------- Count selector ---------- */

      bindCountChange() {
        if (!this.countSelect) return;
        this.countSelect.addEventListener('change', () => {
          this.clearCountError();
          this.applyCount();
        });
      }

      getSelectedCount() {
        const raw = this.countSelect?.value ?? '';
        const count = parseInt(raw, 10);
        if (!Number.isFinite(count) || count < this.minCount) return null;
        return count;
      }

      applyCount() {
        const count = this.getSelectedCount();
        const hasCount = count !== null;

        if (this.rowsContainer) {
          if (hasCount) {
            this.rowsContainer.removeAttribute('hidden');
          } else {
            this.rowsContainer.setAttribute('hidden', '');
          }
        }

        this.rows.forEach((row) => {
          const idx = parseInt(row.dataset.pickerRow, 10);
          const input = row.querySelector('[data-picker-property]');
          const shouldShow = hasCount && idx <= count;
          if (shouldShow) {
            row.removeAttribute('hidden');
            if (input) input.disabled = false;
          } else {
            row.setAttribute('hidden', '');
            if (input) {
              input.disabled = true;
              input.value = '';
            }
            this.resetRowDisplay(row);
            this.clearRowError(row);
          }
        });
      }

      /* ---------- Dropdown UI ---------- */

      bindDropdowns() {
        this.querySelectorAll('[data-picker-trigger]').forEach((trigger) => {
          trigger.addEventListener('click', (e) => {
            e.preventDefault();
            this.toggleDropdown(trigger);
          });
        });

        this.querySelectorAll('[data-picker-item]').forEach((item) => {
          item.addEventListener('click', () => this.selectItem(item));
        });
      }

      toggleDropdown(trigger) {
        const dropdown = trigger.closest('[data-picker-dropdown]');
        if (!dropdown) return;
        const list = dropdown.querySelector('[data-picker-list]');
        if (!list) return;
        const isOpen = !list.hidden;
        this.closeAllDropdowns();
        if (!isOpen) {
          list.hidden = false;
          trigger.setAttribute('aria-expanded', 'true');
        }
      }

      closeAllDropdowns() {
        this.querySelectorAll('[data-picker-list]').forEach((list) => {
          list.hidden = true;
        });
        this.querySelectorAll('[data-picker-trigger]').forEach((trigger) => {
          trigger.setAttribute('aria-expanded', 'false');
        });
      }

      onDocumentClick(e) {
        if (!e.target.closest('[data-picker-dropdown]')) {
          this.closeAllDropdowns();
        }
      }

      onKeydown(e) {
        if (e.key === 'Escape') this.closeAllDropdowns();
      }

      /* ---------- Selection ---------- */

      selectItem(item) {
        const row = item.closest('[data-picker-row]');
        const dropdown = item.closest('[data-picker-dropdown]');
        if (!row || !dropdown) return;

        const trigger = dropdown.querySelector('[data-picker-trigger]');
        const selectedImage = trigger?.querySelector('[data-picker-selected-image]');
        const selectedLabel = trigger?.querySelector('[data-picker-selected-label]');
        const propertyInput = row.querySelector('[data-picker-property]');

        const { title = '', subtitle = '', image = '', value = '' } = item.dataset;

        if (selectedImage) {
          selectedImage.innerHTML = image
            ? `<img src="${image}" alt="" width="28" height="28">`
            : '';
        }

        if (selectedLabel) {
          selectedLabel.innerHTML = subtitle
            ? `<span class="picker-set__selected-title">${this.escapeHtml(title)}</span>` +
              `<span class="picker-set__selected-subtitle">${this.escapeHtml(subtitle)}</span>`
            : `<span class="picker-set__selected-title">${this.escapeHtml(title)}</span>`;
        }

        dropdown.querySelectorAll('[data-picker-item]').forEach((el) => el.classList.remove('is-selected'));
        item.classList.add('is-selected');

        if (propertyInput) propertyInput.value = value;

        this.clearRowError(row);
        this.closeAllDropdowns();
      }

      resetRowDisplay(row) {
        const trigger = row.querySelector('[data-picker-trigger]');
        const selectedImage = trigger?.querySelector('[data-picker-selected-image]');
        const selectedLabel = trigger?.querySelector('[data-picker-selected-label]');
        if (selectedImage) selectedImage.innerHTML = '';
        if (selectedLabel) {
          selectedLabel.innerHTML = `<span class="picker-set__selected-placeholder">${this.escapeHtml(
            this.itemPlaceholder
          )}</span>`;
        }
        row.querySelectorAll('[data-picker-item]').forEach((el) => el.classList.remove('is-selected'));
      }

      /* ---------- Validation ---------- */

      validate() {
        this.clearCountError();
        let isValid = true;

        if (this.getSelectedCount() === null) {
          this.showCountError();
          return false;
        }

        this.rows.forEach((row) => this.clearRowError(row));
        this.rows.forEach((row) => {
          if (row.hidden) return;
          const input = row.querySelector('[data-picker-property]');
          if (!input || !input.value) {
            this.showRowError(row);
            isValid = false;
          }
        });
        return isValid;
      }

      showCountError() {
        if (this.countSelect) {
          this.countSelect.classList.add('picker-set__select--error');
        }
        if (this.countErrorEl) {
          this.countErrorEl.textContent = this.errorMessage;
          this.countErrorEl.hidden = false;
        }
      }

      clearCountError() {
        if (this.countSelect) {
          this.countSelect.classList.remove('picker-set__select--error');
        }
        if (this.countErrorEl) {
          this.countErrorEl.textContent = '';
          this.countErrorEl.hidden = true;
        }
      }

      showRowError(row) {
        row.classList.add('picker-set__row--error');
        const errorEl = row.querySelector('[data-picker-row-error]');
        if (errorEl) {
          errorEl.textContent = this.errorMessage;
          errorEl.hidden = false;
        }
      }

      clearRowError(row) {
        row.classList.remove('picker-set__row--error');
        const errorEl = row.querySelector('[data-picker-row-error]');
        if (errorEl) {
          errorEl.textContent = '';
          errorEl.hidden = true;
        }
      }

      /* ---------- Reset (called by parent after add-to-cart) ---------- */

      reset() {
        if (this.countSelect) this.countSelect.value = '';
        this.clearCountError();
        this.rows.forEach((row) => {
          const input = row.querySelector('[data-picker-property]');
          if (input) input.value = '';
          this.resetRowDisplay(row);
          this.clearRowError(row);
        });
        this.applyCount();
      }

      /* ---------- Utils ---------- */

      escapeHtml(str) {
        return String(str)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      }
    }
  );
}
